import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  TagIcon,
  GiftIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import BundleForm from '../../components/admin/BundleForm';

const AdminBundles = () => {
  const [bundles, setBundles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBundle, setDeletingBundle] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    featured: '',
    active: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  useEffect(() => {
    fetchBundles();
    fetchCategories();
  }, [filters, pagination.currentPage]);

  const fetchBundles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.featured) params.append('featured', filters.featured);
      params.append('sort', filters.sortBy);
      params.append('order', filters.sortOrder);
      params.append('page', pagination.currentPage);
      params.append('limit', pagination.itemsPerPage);

      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/bundles?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBundles(response.data.bundles || []);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (error) {
      console.error('Error fetching bundles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data.allCategories || response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const handleToggleStatus = async (bundleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/bundles/${bundleId}/status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBundles();
    } catch (error) {
      console.error('Error toggling bundle status:', error);
      alert('שגיאה בשינוי סטטוס החבילה');
    }
  };

  const handleDelete = async () => {
    if (!deletingBundle) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/bundles/${deletingBundle.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      setDeletingBundle(null);
      fetchBundles();
    } catch (error) {
      console.error('Error deleting bundle:', error);
      alert('שגיאה במחיקת החבילה');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(price);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('he-IL');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <GiftIcon className="h-8 w-8 text-purple-600" />
              ניהול חבילות
            </h1>
            <p className="text-gray-600 mt-2">
              נהל חבילות מוצרים במחירים מיוחדים
            </p>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-md"
          >
            <PlusIcon className="h-5 w-5" />
            חבילה חדשה
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">סך הכל חבילות</p>
                <p className="text-2xl font-bold text-gray-900">{pagination.totalItems}</p>
              </div>
              <GiftIcon className="h-8 w-8 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">חבילות פעילות</p>
                <p className="text-2xl font-bold text-green-600">
                  {bundles.filter(b => b.is_active).length}
                </p>
              </div>
              <TagIcon className="h-8 w-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">חבילות מומלצות</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {bundles.filter(b => b.is_featured).length}
                </p>
              </div>
              <TagIcon className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">סה"כ מכירות</p>
                <p className="text-2xl font-bold text-blue-600">
                  {bundles.reduce((sum, b) => sum + (b.sales_count || 0), 0)}
                </p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="חפש חבילה..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Filter */}
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="">כל הקטגוריות</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          {/* Featured Filter */}
          <select
            value={filters.featured}
            onChange={(e) => setFilters({ ...filters, featured: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="">הכל</option>
            <option value="true">מומלץ</option>
            <option value="false">רגיל</option>
          </select>

          {/* Sort */}
          <select
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="created_at">תאריך יצירה</option>
            <option value="sales_count">מכירות</option>
            <option value="views">צפיות</option>
            <option value="bundle_price">מחיר</option>
          </select>

          <button
            onClick={() => setFilters({
              search: '',
              category: '',
              featured: '',
              active: '',
              sortBy: 'created_at',
              sortOrder: 'desc'
            })}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            נקה פילטרים
          </button>
        </div>
      </div>

      {/* Bundles Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : bundles.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <GiftIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">אין חבילות</h3>
          <p className="text-gray-600 mb-6">התחל ליצור חבילות כדי להגדיל מכירות</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            צור חבילה ראשונה
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bundles.map((bundle) => (
            <div
              key={bundle.id}
              className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Bundle Image */}
              <div className="relative h-48 bg-gray-200">
                {bundle.bundle_image || bundle.images?.[0] ? (
                  <img
                    src={`/api${bundle.bundle_image || bundle.images[0]}`}
                    alt={bundle.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <GiftIcon className="h-16 w-16 text-gray-400" />
                  </div>
                )}
                
                {/* Badges */}
                <div className="absolute top-2 right-2 flex gap-2">
                  {bundle.is_featured && (
                    <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-semibold rounded">
                      מומלץ
                    </span>
                  )}
                  {!bundle.is_active && (
                    <span className="px-2 py-1 bg-red-500 text-white text-xs font-semibold rounded">
                      לא פעיל
                    </span>
                  )}
                  {bundle.discount_percentage > 0 && (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded">
                      -{Math.round(bundle.discount_percentage)}%
                    </span>
                  )}
                </div>
              </div>

              {/* Bundle Details */}
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                  {bundle.title}
                </h3>
                
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {bundle.description || 'אין תיאור'}
                </p>

                {/* Items Count */}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <TagIcon className="h-4 w-4" />
                  <span>{bundle.items?.length || 0} מוצרים</span>
                </div>

                {/* Pricing */}
                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 line-through">
                      {formatPrice(bundle.regular_price)}
                    </span>
                    <span className="text-2xl font-bold text-purple-600">
                      {formatPrice(bundle.bundle_price)}
                    </span>
                  </div>
                  <div className="text-sm text-green-600 font-semibold">
                    חסכון של {formatPrice(bundle.regular_price - bundle.bundle_price)}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex gap-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-1">
                    <EyeIcon className="h-4 w-4" />
                    <span>{bundle.views || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <ChartBarIcon className="h-4 w-4" />
                    <span>{bundle.sales_count || 0} מכירות</span>
                  </div>
                </div>

                {/* Validity */}
                {(bundle.valid_from || bundle.valid_until) && (
                  <div className="text-xs text-gray-500 mb-4">
                    {bundle.valid_from && `מ-${formatDate(bundle.valid_from)}`}
                    {bundle.valid_from && bundle.valid_until && ' '}
                    {bundle.valid_until && `עד ${formatDate(bundle.valid_until)}`}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingBundle(bundle);
                      setShowEditModal(true);
                    }}
                    className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                  >
                    <PencilIcon className="h-4 w-4" />
                    ערוך
                  </button>
                  
                  <button
                    onClick={() => handleToggleStatus(bundle.id)}
                    className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                      bundle.is_active
                        ? 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                  >
                    {bundle.is_active ? 'השבת' : 'הפעל'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setDeletingBundle(bundle);
                      setShowDeleteModal(true);
                    }}
                    className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-8 flex justify-center gap-2">
          <button
            onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
            disabled={pagination.currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            הקודם
          </button>
          
          <span className="px-4 py-2">
            עמוד {pagination.currentPage} מתוך {pagination.totalPages}
          </span>
          
          <button
            onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
            disabled={pagination.currentPage === pagination.totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            הבא
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">אישור מחיקה</h3>
            <p className="text-gray-600 mb-6">
              האם אתה בטוח שברצונך למחוק את החבילה "{deletingBundle?.title}"?
              פעולה זו לא ניתנת לביטול.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                מחק
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingBundle(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modals */}
      {showCreateModal && (
        <BundleForm
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            fetchBundles();
          }}
        />
      )}

      {showEditModal && editingBundle && (
        <BundleForm
          bundle={editingBundle}
          onClose={() => {
            setShowEditModal(false);
            setEditingBundle(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setEditingBundle(null);
            fetchBundles();
          }}
        />
      )}
    </div>
  );
};

export default AdminBundles;

